/**
 * /admin/system/alerts — 系统告警中心
 *
 * T-S2-B-4 · 业务告警 + 健康状态
 *
 * 功能：
 *  - 4 类告警统计磁贴（爆仓 / 浪费 / 短板 / 利润）
 *  - 告警类型 chip 过滤
 *  - 告警列表 table（时间 / 类型 / 严重程度 / 标题 / 详情 / 处理状态）
 *  - 健康状态卡片（DB / Redis 状态，来自 /api/v1/health）
 *  - "触发检查" button → POST /api/v1/alerts/run-checks
 */
import { useState, useEffect, useCallback } from "react";
import {
  AlertTriangle,
  CheckCircle2,
  RefreshCw,
  Loader2,
  Database,
  Server,
  TrendingDown,
  BoxIcon,
  BatteryWarning,
  BarChart2,
} from "lucide-react";
import { PageShell, Pill, LoadingState, EmptyState } from "@/shared/ui/PageHeader";
import { useAuthStore } from "@/store/authStore";

// ─── Types ────────────────────────────────────────────────────────────────────

interface AlertItem {
  id: string;
  at_time: string;
  alert_type: AlertType;
  severity: "warning" | "critical";
  title: string;
  detail: string;
  entity_type: string;
  entity_id: string;
  status: "open" | "resolved";
}

interface AlertListResp {
  total: number;
  offset: number;
  limit: number;
  items: AlertItem[];
}

interface HealthResp {
  status: "ok" | "degraded";
  db: string;
  redis: string;
  timestamp: string;
}

interface RunChecksResp {
  status: "ok" | "error";
  fired: number;
  alerts: AlertItem[];
}

type AlertType =
  | "inventory_overflow"
  | "inventory_waste"
  | "bottleneck"
  | "low_profit"
  | "";

// ─── Helpers ──────────────────────────────────────────────────────────────────

function apiHeaders(): HeadersInit {
  const token = useAuthStore.getState().accessToken;
  return {
    "Content-Type": "application/json",
    "X-Tenant-Slug": "default",
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

const ALERT_TYPE_LABELS: Record<string, string> = {
  inventory_overflow: "爆仓",
  inventory_waste:    "浪费",
  bottleneck:         "短板",
  low_profit:         "利润",
};

const ALERT_TYPE_FILTERS: { value: AlertType; label: string }[] = [
  { value: "",                    label: "全部" },
  { value: "inventory_overflow",  label: "爆仓" },
  { value: "inventory_waste",     label: "浪费" },
  { value: "bottleneck",          label: "短板" },
  { value: "low_profit",          label: "利润" },
];

function severityBadge(severity: string) {
  if (severity === "critical") {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-red-50 px-2 py-0.5 text-[11px] font-semibold text-red-700">
        <AlertTriangle className="h-3 w-3" />
        紧急
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-semibold text-amber-700">
      <AlertTriangle className="h-3 w-3" />
      警告
    </span>
  );
}

function alertTypeChip(alert_type: string) {
  const label = ALERT_TYPE_LABELS[alert_type] ?? alert_type;
  const cls: Record<string, string> = {
    inventory_overflow: "bg-red-50 text-red-700",
    inventory_waste:    "bg-amber-50 text-amber-700",
    bottleneck:         "bg-orange-50 text-orange-700",
    low_profit:         "bg-purple-50 text-purple-700",
  };
  const color = cls[alert_type] ?? "bg-slate-100 text-slate-600";
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold ${color}`}>
      {label}
    </span>
  );
}

// ─── Stat tile ────────────────────────────────────────────────────────────────

interface StatTileProps {
  icon: React.ReactNode;
  label: string;
  count: number;
  color: string;
}

function StatTile({ icon, label, count, color }: StatTileProps) {
  return (
    <div className={`rounded-xl border bg-white p-4 shadow-sm ${color}`}>
      <div className="flex items-center gap-3">
        <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-current bg-opacity-10">
          {icon}
        </div>
        <div>
          <p className="text-xs text-slate-500">{label}</p>
          <p className="text-2xl font-bold text-slate-900">{count}</p>
        </div>
      </div>
    </div>
  );
}

// ─── Health card ──────────────────────────────────────────────────────────────

function HealthCard({ health, failed }: { health: HealthResp | null; failed?: boolean }) {
  if (!health) {
    return (
      <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
        <p className="text-sm text-slate-500">{failed ? "健康状态加载失败" : "健康状态加载中…"}</p>
      </div>
    );
  }
  const dbOk   = health.db    === "ok";
  const redisOk = health.redis === "ok";
  const overall = health.status === "ok";

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className="mb-3 flex items-center justify-between">
        <h3 className="text-sm font-semibold text-slate-700">系统健康状态</h3>
        <span
          className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold ${
            overall ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"
          }`}
        >
          {overall ? <CheckCircle2 className="h-3 w-3" /> : <AlertTriangle className="h-3 w-3" />}
          {overall ? "正常" : "异常"}
        </span>
      </div>
      <div className="flex flex-col gap-2">
        <div className="flex items-center gap-2 text-sm">
          <Database className="h-4 w-4 text-slate-400" />
          <span className="text-slate-600">数据库</span>
          <span
            className={`ml-auto font-medium ${dbOk ? "text-emerald-600" : "text-red-600"}`}
          >
            {dbOk ? "正常" : health.db}
          </span>
        </div>
        <div className="flex items-center gap-2 text-sm">
          <Server className="h-4 w-4 text-slate-400" />
          <span className="text-slate-600">缓存 (Redis)</span>
          <span
            className={`ml-auto font-medium ${redisOk ? "text-emerald-600" : "text-amber-600"}`}
          >
            {redisOk ? "正常" : health.redis}
          </span>
        </div>
      </div>
      <p className="mt-3 text-[11px] text-slate-400">
        检查时间：{new Date(health.timestamp).toLocaleString("zh-CN")}
      </p>
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

export function AlertsPage() {
  const [alerts,     setAlerts]     = useState<AlertItem[]>([]);
  const [total,      setTotal]      = useState(0);
  const [health,     setHealth]     = useState<HealthResp | null>(null);
  const [healthFailed, setHealthFailed] = useState(false);
  const [filter,     setFilter]     = useState<AlertType>("");
  const [loading,    setLoading]    = useState(false);
  const [checking,   setChecking]   = useState(false);
  const [checkMsg,   setCheckMsg]   = useState<string | null>(null);
  const [error,      setError]      = useState<string | null>(null);

  const loadAlerts = useCallback(async (alertType: AlertType = "") => {
    setLoading(true);
    setError(null);
    try {
      const qs = alertType ? `?alert_type=${alertType}&limit=50` : "?limit=50";
      const r = await fetch(`/api/v1/alerts${qs}`, { headers: apiHeaders() });
      if (!r.ok) throw new Error(`${r.status} ${r.statusText}`);
      const data: AlertListResp = await r.json();
      setAlerts(data.items);
      setTotal(data.total);
    } catch (e) {
      setError(`加载告警失败: ${String(e)}`);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadHealth = useCallback(async () => {
    try {
      const r = await fetch("/api/v1/health");
      if (r.ok) { setHealth(await r.json()); setHealthFailed(false); }
      else setHealthFailed(true);
    } catch {
      setHealthFailed(true);
    }
  }, []);

  useEffect(() => {
    loadAlerts(filter);
    loadHealth();
  }, [filter, loadAlerts, loadHealth]);

  const handleRunChecks = async () => {
    setChecking(true);
    setCheckMsg(null);
    setError(null);
    try {
      const r = await fetch("/api/v1/alerts/run-checks", {
        method: "POST",
        headers: apiHeaders(),
      });
      const data: RunChecksResp = await r.json();
      if (r.ok) {
        setCheckMsg(`检查完成，触发 ${data.fired} 条告警`);
        await loadAlerts(filter);
        await loadHealth();
      } else {
        setError(`触发检查失败 (${r.status})`);
      }
    } catch (e) {
      setError(`触发检查异常: ${String(e)}`);
    } finally {
      setChecking(false);
    }
  };

  // Stat counts by type
  const countByType = (t: string) =>
    alerts.filter((a) => a.alert_type === t).length;

  return (
    <PageShell
      title="系统告警"
      subtitle={`系统 / 告警 · 共 ${total} 条`}
      actions={
        <button
          onClick={handleRunChecks}
          disabled={checking}
          className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {checking ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <RefreshCw className="h-4 w-4" />
          )}
          {checking ? "检查中…" : "触发检查"}
        </button>
      }
    >
      {/* Stat tiles */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <StatTile
          icon={<BoxIcon className="h-4 w-4 text-red-600" />}
          label="爆仓告警"
          count={countByType("inventory_overflow")}
          color="border-red-100"
        />
        <StatTile
          icon={<BatteryWarning className="h-4 w-4 text-amber-600" />}
          label="浪费告警"
          count={countByType("inventory_waste")}
          color="border-amber-100"
        />
        <StatTile
          icon={<AlertTriangle className="h-4 w-4 text-orange-600" />}
          label="短板告警"
          count={countByType("bottleneck")}
          color="border-orange-100"
        />
        <StatTile
          icon={<TrendingDown className="h-4 w-4 text-purple-600" />}
          label="利润告警"
          count={countByType("low_profit")}
          color="border-purple-100"
        />
      </div>

      {/* Type filter chips */}
      <div className="flex flex-wrap gap-2">
        {ALERT_TYPE_FILTERS.map(({ value, label }) => (
          <button
            key={value}
            onClick={() => setFilter(value)}
            className={`rounded-full px-3 py-1 text-xs font-medium transition ${
              filter === value
                ? "bg-blue-600 text-white shadow-sm"
                : "bg-slate-100 text-slate-600 hover:bg-slate-200"
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {/* Feedback messages */}
      {checkMsg && (
        <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-2.5 text-sm text-emerald-800">
          <CheckCircle2 className="mr-2 inline h-4 w-4" />
          {checkMsg}
        </div>
      )}
      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-2.5 text-sm text-red-800">
          <AlertTriangle className="mr-2 inline h-4 w-4" />
          {error}
        </div>
      )}

      {/* Alert table */}
      {loading ? (
        <LoadingState />
      ) : alerts.length === 0 ? (
        <EmptyState message="暂无告警记录" />
      ) : (
        <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs font-semibold uppercase text-slate-500">
              <tr>
                <th className="px-4 py-3 text-left">时间</th>
                <th className="px-4 py-3 text-left">类型</th>
                <th className="px-4 py-3 text-left">严重程度</th>
                <th className="px-4 py-3 text-left">标题</th>
                <th className="px-4 py-3 text-left hidden lg:table-cell">详情</th>
                <th className="px-4 py-3 text-left">处理状态</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {alerts.map((a) => (
                <tr key={a.id} className="hover:bg-slate-50">
                  <td className="px-4 py-3 text-xs text-slate-500 whitespace-nowrap">
                    {new Date(a.at_time).toLocaleString("zh-CN")}
                  </td>
                  <td className="px-4 py-3">{alertTypeChip(a.alert_type)}</td>
                  <td className="px-4 py-3">{severityBadge(a.severity)}</td>
                  <td className="px-4 py-3 font-medium text-slate-900 max-w-xs truncate">
                    {a.title}
                  </td>
                  <td className="px-4 py-3 text-slate-500 max-w-xs truncate hidden lg:table-cell">
                    {a.detail}
                  </td>
                  <td className="px-4 py-3">
                    <span className="inline-flex items-center rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-600">
                      {a.status === "resolved" ? "已处理" : "待处理"}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Health status card */}
      <HealthCard health={health} failed={healthFailed} />
    </PageShell>
  );
}
